import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { Oval } from "react-loader-spinner";
import { toast } from "react-toastify";
import axiosInstance from "../../../../hooks/axiosInstance";

const STATUS_BADGE_CLASS = {
  pending: "bg-warning",
  sent: "bg-success",
  failed: "bg-danger",
  partial: "bg-info",
};

function StatusBadge({ status }) {
  return (
    <span className={`badge ${STATUS_BADGE_CLASS[status] || "bg-secondary"}`}>
      {status}
    </span>
  );
}

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString();
}

export default function ViewMessage({ showView, closeModal, messageId }) {
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setMessage(null);
    closeModal();
  };

  useEffect(() => {
    const fetchMessage = async () => {
      if (!showView || !messageId) {
        return;
      }

      setLoading(true);

      try {
        const response = await axiosInstance.get(`/user/messaging/${messageId}`);
        setMessage(response.data.data.message);
      } catch (error) {
        toast.error("Failed to load message details.");
      } finally {
        setLoading(false);
      }
    };

    fetchMessage();
  }, [showView, messageId]);

  return (
    <div className="ViewMessage">
      <Modal show={showView} onHide={handleClose} dialogClassName="modal-lg">
        <Modal.Header closeButton>
          <Modal.Title>Message Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "150px" }}>
              <Oval height={50} width={50} color="#FF0200" visible={true} secondaryColor="#4fa94d" />
            </div>
          ) : message ? (
            <>
              <p>
                <strong>Subject:</strong> {message.subject}
              </p>
              <p>
                <strong>Recipient Type:</strong> {message.recipient_type} ({message.recipient_scope})
              </p>
              <p>
                <strong>Status:</strong> <StatusBadge status={message.status} /> &nbsp;
                <strong>Recipients:</strong> {message.recipient_count}
              </p>
              <p style={{ whiteSpace: "pre-wrap" }}>
                <strong>Body:</strong>
                <br />
                {message.body}
              </p>

              <hr />

              <h6 className="mb-2">Delivery Recipients</h6>
              <div className="table-responsive">
                <table className="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(message.recipients || []).map((recipient) => (
                      <tr key={recipient.id}>
                        <td>{recipient.recipient_email}</td>
                        <td>
                          <StatusBadge status={recipient.status} />
                        </td>
                        <td className="text-danger">{recipient.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <hr />

              <div className="d-flex align-items-center gap-2 mb-3">
                <h6 className="mb-0">Replies</h6>
                <span
                  className="badge rounded-pill"
                  style={{ background: "#405189", fontSize: "0.75rem" }}
                >
                  {(message.replies || []).length}
                </span>
              </div>
              {(message.replies || []).length === 0 ? (
                <div
                  className="text-center py-4 rounded"
                  style={{ background: "#f8f9fa", border: "1px dashed #dee2e6" }}
                >
                  <i className="ri-chat-3-line" style={{ fontSize: "1.8rem", color: "#adb5bd" }}></i>
                  <p className="text-muted small mt-2 mb-0">No replies received yet.</p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {message.replies.map((reply) => (
                    <div
                      key={reply.id}
                      className="d-flex gap-3"
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: 38,
                          height: 38,
                          background: "#405189",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                        }}
                      >
                        {reply.from_email.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-grow-1">
                        <div
                          className="rounded p-3"
                          style={{ background: "#f3f6f9", border: "1px solid #e9ecef" }}
                        >
                          <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-1">
                            <span style={{ fontWeight: 600, fontSize: "0.875rem", color: "#1A2E44" }}>
                              {reply.from_email}
                            </span>
                            <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                              {formatDateTime(reply.replied_at)}
                            </span>
                          </div>
                          <div
                            style={{
                              whiteSpace: "pre-wrap",
                              fontSize: "0.875rem",
                              color: "#444",
                              lineHeight: 1.6,
                            }}
                          >
                            {reply.body_plain || "— (no plain text body) —"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p>No message selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
